import { Text, TextColor, TextSize, TextWeight } from '@vizit/atoms';
import type { IconProps } from '@vizit/icons';
import { colors, cx } from '@vizit/theme';
import type React from 'react';

interface LibraryEmptyStateProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * The children to display
   * @type {React.ReactNode}
   * @optional
   */
  children?: React.ReactNode;
  /**
   * The heading text to display
   * @type {string}
   * @optional
   * @default "You don't have any assets yet"
   */
  heading?: string;
  /**
   * The description text to display
   * @type {string}
   * @optional
   * @default "Start by scoring your first image!"
   */
  description?: string;
  /**
   * The button text to display
   * @type {string}
   * @optional
   * @default "Score product"
   */
  buttonText?: string;
  /**
   * Button click handler
   * @type {() => void}
   * @optional
   */
  onButtonClick?: () => void;
  /**
   * Whether the button is disabled
   * @type {boolean}
   * @optional
   * @default false
   */
  isButtonDisabled?: boolean;
  /**
   * Whether the button is loading
   * @type {boolean}
   * @optional
   * @default false
   */
  isButtonLoading?: boolean;
  /**
   * Custom icon to display
   * @type {React.ReactNode}
   * @optional
   */
  icon?: React.ComponentType<IconProps>;
}

/**
 * LibraryEmptyState component displays a message when there are no items to show in a library/collection view.
 * Suitable for both assets and products empty states.
 *
 * @param props - The props for the LibraryEmptyState component.
 * @example
 * <LibraryEmptyState
 *   heading="You don't have any assets yet"
 *   description="Start by scoring your first image!"
 *   buttonText="Score an image"
 *   onButtonClick={() => console.log('Button clicked')}
 * />
 */
function LibraryEmptyState(props: LibraryEmptyStateProps) {
  const {
    children,
    heading = "You don't have any assets yet",
    description = 'Start by scoring your first image!',
    className,
    icon: Icon,
    ...other
  } = props;

  return (
    <div className={cx('flex flex-col items-center justify-center text-center p-8', className)} {...other}>
      {Icon && (
        <div
          role="img"
          aria-label="Empty state illustration"
          className="flex items-center justify-center w-24 h-24 p-6 border border-primary mb-8"
        >
          <Icon size={24} color={colors['gray-utility']['700']} aria-hidden="true" />
        </div>
      )}

      <Text
        elementType="h3"
        size={TextSize.Lg}
        weight={TextWeight.Medium}
        color={TextColor.Primary}
        className="max-w-sm"
        showEllipsis={false}
      >
        {heading}
      </Text>

      <Text
        showEllipsis={false}
        elementType="p"
        size={TextSize.Sm}
        color={TextColor.Tertiary}
        className="mb-8 max-w-sm"
      >
        {description}
      </Text>

      {children}
    </div>
  );
}

export type { LibraryEmptyStateProps };
export { LibraryEmptyState };
